import React, { useState, useEffect } from 'react';
import { colors, getAvatarColor } from '../utils/colors';
import { verifyPin } from '../Api/auth';
import type { TeacherListItem } from '../utils/types';

interface Props {
  teacher: TeacherListItem;
  onSuccess: () => void;
  onCancel: () => void;
}

const TITLES = new Set([
  'mr', 'mrs', 'ms', 'miss', 'mx',
  'dr', 'prof', 'sir', 'madam', 'mam',
  'smt', 'shri', 'sri', 'shree',
]);

function initials(name: string) {
  const words = name
    .split(/\s+/)
    .map(w => w.replace(/\.$/, ''))
    .filter(w => w && !TITLES.has(w.toLowerCase()));
  return words.slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

const PIN_LENGTH = 6;

const TeacherPinDrawer: React.FC<Props> = ({ teacher, onSuccess, onCancel }) => {
  const [pin, setPin] = useState('');
  const [shake, setShake] = useState(false);
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleKey = (key: string) => {
    if (loading) return;
    if (key === 'back') {
      setPin(p => p.slice(0, -1));
      setError('');
      return;
    }
    if (pin.length >= PIN_LENGTH) return;
    const next = pin + key;
    setPin(next);
    setError('');

    if (next.length === PIN_LENGTH) {
      validatePin(next);
    }
  };

  const validatePin = async (entered: string) => {
    setLoading(true);
    try {
      await verifyPin(teacher.id, entered);
      onSuccess();
    } catch (err: any) {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setPin('');
      setShake(true);
      setError(newAttempts >= 2 ? `Incorrect PIN. ${3 - newAttempts} attempt${3 - newAttempts !== 1 ? 's' : ''} remaining.` : 'Incorrect PIN. Try again.');
      setTimeout(() => setShake(false), 600);
      if (newAttempts >= 3) {
        setTimeout(() => {
          setError('');
          onCancel();
        }, 1200);
      }
    } finally {
      setLoading(false);
    }
  };

  const keys = [
    ['1','2','3'],
    ['4','5','6'],
    ['7','8','9'],
    ['back','0','ok'],
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onCancel}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 200 }}
      />

      {/* Sheet */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: '#fff', borderRadius: '24px 24px 0 0',
        zIndex: 201, paddingBottom: 'env(safe-area-inset-bottom, 20px)',
        animation: 'sheetSlideUp 0.32s cubic-bezier(0.22,1,0.36,1) both',
      }}>
        {/* Handle */}
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 14, paddingBottom: 6 }}>
          <div style={{ width: 40, height: 4, background: colors.border, borderRadius: 4 }} />
        </div>

        {/* Teacher info */}
        <div style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%', flexShrink: 0,
            background: getAvatarColor(teacher.name),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20, fontWeight: 800, color: '#fff',
          }}>
            {initials(teacher.name)}
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: colors.text }}>{teacher.name}</div>
            <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 2 }}>ID: {teacher.id}</div>
          </div>
        </div>

        <div style={{ height: 1, background: colors.border, margin: '0 24px' }} />

        {/* PIN section */}
        <div style={{ padding: '20px 24px 0' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: colors.textMuted, letterSpacing: '0.5px', textAlign: 'center', marginBottom: 18 }}>
            {loading ? 'VERIFYING…' : 'ENTER YOUR 6-DIGIT PIN'}
          </div>

          {/* PIN dots */}
          <div style={{
            display: 'flex', justifyContent: 'center', gap: 16, marginBottom: 8,
            animation: shake ? 'shake 0.5s ease' : 'none',
          }}>
            {Array.from({ length: PIN_LENGTH }).map((_, i) => {
              const filled = i < pin.length;
              return (
                <div key={i} style={{
                  width: 18, height: 18, borderRadius: '50%',
                  background: filled ? colors.primaryDark : 'transparent',
                  border: `2.5px solid ${filled ? colors.primaryDark : colors.border}`,
                  transition: 'background-color 0.12s linear, border-color 0.12s linear',
                }} />
              );
            })}
          </div>

          {/* Error message */}
          <div style={{ height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
            {error && (
              <div style={{ fontSize: 13, color: colors.danger, fontWeight: 500, textAlign: 'center', animation: 'fadeIn 0.2s ease' }}>
                {error}
              </div>
            )}
          </div>
        </div>

        {/* Keypad */}
        <div style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {keys.map((row, ri) => (
            <div key={ri} style={{ display: 'flex', gap: 10 }}>
              {row.map(key => {
                const isBack = key === 'back';
                const isOk   = key === 'ok';
                return (
                  <button
                    key={key}
                    onClick={() => handleKey(key)}
                    disabled={isOk}
                    className="pin-key"
                    style={{
                      flex: 1, height: 58, borderRadius: 14, cursor: isOk ? 'default' : 'pointer',
                      background: isBack ? colors.bg : isOk ? 'transparent' : '#fff',
                      border: isBack ? `1px solid ${colors.border}` : isOk ? 'none' : `1px solid ${colors.border}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: isBack ? 18 : 22, fontWeight: 700, color: colors.text,
                      WebkitTapHighlightColor: 'transparent',
                    } as React.CSSProperties}
                  >
                    {isBack ? (
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={colors.textMuted} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6"/>
                      </svg>
                    ) : isOk ? null : key}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Cancel button */}
        <div style={{ padding: '0 20px 12px' }}>
          <button onClick={onCancel} style={{
            width: '100%', padding: '14px', background: colors.bg, border: `1px solid ${colors.border}`,
            borderRadius: 14, fontSize: 15, fontWeight: 600, cursor: 'pointer', color: colors.textMuted,
          }}>
            Cancel
          </button>
        </div>
      </div>
    </>
  );
};

export default TeacherPinDrawer;
